'use client';

import 'client-only';
import { useDeepCompareEffect, useDeepCompareMemo } from '@react-hookz/web';
import { useState } from 'react';

type ArrayLengthMutationKeys = 'splice' | 'push' | 'pop' | 'shift' | 'unshift';
type FixedLengthArray<
  T,
  L extends number = number,
  TObj = [T, ...Array<T>],
> = Pick<TObj, Exclude<keyof TObj, ArrayLengthMutationKeys>> & {
  readonly length: L;
  [Symbol.iterator]: () => IterableIterator<T>;
};

const checkBreakpoint = (breakpoint: number): boolean =>
  window.innerWidth >= breakpoint;

type BreakpointResult<L extends number, A> = A extends undefined
  ? FixedLengthArray<boolean, 2>
  : FixedLengthArray<boolean, L>;

const useBreakpoint = <
  L extends number,
  A extends FixedLengthArray<number, L> | undefined = undefined,
>(
  breakpoints?: A,
): BreakpointResult<L, A> => {
  if (typeof window === 'undefined') {
    throw new Error('useBreakpoint can only be used in client side.');
  }

  const breakpointCheckList = useDeepCompareMemo(
    () => breakpoints || [600, 1440],
    [breakpoints],
  );

  const [breakpointResult, setBreakpointResult] = useState<
    BreakpointResult<L, A>
  >(
    breakpointCheckList.map(checkBreakpoint) as unknown as BreakpointResult<
      L,
      A
    >,
  );

  useDeepCompareEffect(() => {
    const handleResize = () => {
      const newBreakpointResult = breakpointCheckList.map(
        checkBreakpoint,
      ) as unknown as BreakpointResult<L, A>;

      setBreakpointResult((prevBreakpointResult) => {
        if (
          newBreakpointResult.some(
            (result, index) => result !== prevBreakpointResult[index],
          )
        ) {
          return newBreakpointResult;
        }
        return prevBreakpointResult;
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [breakpointCheckList]);

  return breakpointResult;
};

export default useBreakpoint;
